<template>
  <div class="component bg-light">
    <b-breadcrumb :items="items"></b-breadcrumb>

    <div class="container-fluid px-2">
      <USRiskManagementFilters :start-collapsed="startCollapsed" />

      <USMultiselectInput
        class="px-2 pb-3 col-xl-4 col-12"
        id="risk-detail-table-option-select"
        aria-label="Select de opções de gestão de riscos detalhado"
        :options="tableOptions"
        v-model="selectedTableOption"
        :show-label="false"
        placeholder="Selecione uma opção"
        :searchable="false"
      />
    </div>

    <div v-if="selectedTableOption !== null">
      <table class="table table-bordered text-center">
        <thead class="thead-light">
          <tr>
            <th v-for="column in visibleColumns" :key="column.field">
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in selectedRows" :key="row.field">
            <td v-for="column in visibleColumns" :key="column.field">
              {{ row[column.field] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import USRiskManagementFilters from "@/components/RiskManagement/USRiskManagementFilters.vue";
import USMultiselectInput from "@/components/ui/USMultiselectInput.vue";
import {
  fetchRisksPercentageByHazard,
  fetchMachinesCountAndInvestmentsByHazard,
  fetchInvestedPercentileByHazard,
} from "@/services/risk-management";
import { mapState } from "vuex";
import { types as companyReportTypes } from "@/store/modules/company-report";
import { HazardLevel, HazardLevelTranslated } from "@/enum/HazardLevel";

export default {
  components: {
    USRiskManagementFilters,
    USMultiselectInput,
  },
  name: "RiskDetails",
  props: {
    selected: {
      type: String,
      default: null,
    },
  },

  mounted() {
    this.selectedTableOption = this.selected;
  },

  data() {
    return {
      startCollapsed: false,
      items: [
        { text: "Início", to: "/admin/Home" },
        { text: "Gestão de Riscos", to: "/admin/RiskManagement" },
        { text: "Visão detalhada", active: true },
      ],

      tableOptions: [
        "Comparação de Riscos",
        "Investimentos Pendentes",
        "Investimentos Realizados",
      ],

      selectedTableOption: null,
      selectedColumns: [],
      selectedRows: [],

      investmentsTable: {
        columns: [
          {
            show: true,
            label: "Categoria",
            field: "category",
          },
          {
            show: true,
            label: "Valor Investido",
            field: "invested",
          },
          {
            show: true,
            label: "Percentual Investido",
            field: "investedPercentage",
          },
        ],
        rows: [],
      },

      machinesCountAndInvestmentsByHazardTable: {
        columns: [
          {
            show: true,
            label: "Categoria",
            field: "category",
          },
          {
            show: true,
            label: "Qtd. Máquinas",
            field: "machinesCountmachinesWithPendingPlansCount",
          },
          {
            show: true,
            label: "Valor Pendente",
            field: "budget",
          },
        ],
        rows: [],
      },

      riskComparisonTable: {
        columns: [
          {
            show: true,
            label: "Categoria",
            field: "category",
          },
          {
            show: true,
            label: "Qtd. Atual",
            field: "currentQuantity",
          },
          {
            show: true,
            label: "% Atual",
            field: "currentPercentage",
          },
          {
            show: true,
            label: "Qtd. Após",
            field: "afterQuantity",
          },
          {
            show: true,
            label: "% Após",
            field: "afterPercentage",
          },
        ],
        rows: [],
      },
    };
  },

  computed: {
    ...mapState(companyReportTypes.STORE_MODULE, {
      unit: companyReportTypes.UNIT,
      businessType: companyReportTypes.BUSINESS_TYPE,
      sector: companyReportTypes.SECTOR,
      machine: companyReportTypes.MACHINE,
      corporateGroup: companyReportTypes.CORPORATE_GROUP,
    }),

    unitSelected() {
      return this.unit.selected;
    },
    businessTypeSelected() {
      return this.businessType.selected;
    },
    sectorSelected() {
      return this.sector.selected;
    },
    machineSelected() {
      return this.machine.selected;
    },
    corporateGroupSelected() {
      return this.corporateGroup.selected;
    },
    selectedFilters() {
      return {
        corporateGroupSelected: this.corporateGroupSelected,
        businessTypeSelected: this.businessTypeSelected,
        sectorSelected: this.sectorSelected,
        machineSelected: this.machineSelected,
        unitSelected: this.unitSelected,
        selectedTableOption: this.selectedTableOption,
      };
    },
    visibleColumns() {
      if (this.selectedColumns.length === 0) return [];
      return this.selectedColumns.filter((column) => column.show);
    },
  },
  methods: {
    getHazardLevelTranslated(key) {
      const entry = Object.entries(HazardLevel).find(([, v]) => v === key);
      return entry ? HazardLevelTranslated[entry[0]] : "";
    },

    async loadRiskComparisonPercentageByHazard() {
      try {
        const res = await fetchRisksPercentageByHazard({
          unitId: this.unitSelected?.id,
          corporateGroupId: this.corporateGroupSelected?.id,
          sector: this.sectorSelected?.id,
          businessType: this.businessTypeSelected?.id,
          machineId: this.machineSelected?.id,
        });
        const risksPercentageByHazard = res.data.risksPercentageByHazard;
        const rows = Object.entries(risksPercentageByHazard).map(
          ([key, value]) => ({
            category: this.getHazardLevelTranslated(key),
            currentQuantity: value.currentCount,
            currentPercentage: value.currentScore + "%",
            afterQuantity: value.afterCount,
            afterPercentage: value.afterScore + "%",
          })
        );
        this.riskComparisonTable.rows = rows;
      } catch (error) {
        this.$bvToast.toast("Erro ao buscar dados de risco", {
          variant: "danger",
          title: "Ocorreu um erro",
          solid: true,
        });
      }
    },

    async loadMachinesCountAndInvestmentsByHazard() {
      try {
        const res = await fetchMachinesCountAndInvestmentsByHazard({
          unitId: this.unitSelected?.id,
          corporateGroupId: this.corporateGroupSelected?.id,
          sector: this.sectorSelected?.id,
          businessType: this.businessTypeSelected?.id,
          machineId: this.machineSelected?.id,
        });
        const machinesCountAndInvestmentsByHazard =
          res.data.machinesCountAndInvestmentsByHazard;
        const rows = Object.entries(machinesCountAndInvestmentsByHazard).map(
          ([key, value]) => ({
            category: this.getHazardLevelTranslated(key),
            machinesCountmachinesWithPendingPlansCount:
              value.machinesWithPendingPlansCount,
            budget: "R$ " + value.budget,
          })
        );
        this.machinesCountAndInvestmentsByHazardTable.rows = rows;
      } catch (error) {
        this.$bvToast.toast("Erro ao buscar dados de risco", {
          variant: "danger",
          title: "Ocorreu um erro",
          solid: true,
        });
      }
    },

    async loadInvestedPercentileByHazard() {
      try {
        const res = await fetchInvestedPercentileByHazard({
          unitId: this.unitSelected?.id,
          corporateGroupId: this.corporateGroupSelected?.id,
          sector: this.sectorSelected?.id,
          businessType: this.businessTypeSelected?.id,
          machineId: this.machineSelected?.id,
        });
        const investedPercentileByHazard = res.data.investedPercentileByHazard;
        const rows = Object.entries(investedPercentileByHazard).map(
          ([key, value]) => ({
            category: this.getHazardLevelTranslated(key),
            invested: "R$ " + value.invested,
            investedPercentage: value.percent + "%",
          })
        );
        this.investmentsTable.rows = rows;
      } catch (error) {
        this.$bvToast.toast("Erro ao buscar dados de risco", {
          variant: "danger",
          title: "Ocorreu um erro",
          solid: true,
        });
      }
    },
  },

  watch: {
    selectedFilters: {
      async handler({ selectedTableOption }) {
        if (selectedTableOption === null) return;
        switch (selectedTableOption) {
          case "Comparação de Riscos":
            await this.loadRiskComparisonPercentageByHazard();
            this.selectedColumns = this.riskComparisonTable.columns;
            this.selectedRows = this.riskComparisonTable.rows;
            break;
          case "Investimentos Pendentes":
            await this.loadMachinesCountAndInvestmentsByHazard();
            this.selectedColumns =
              this.machinesCountAndInvestmentsByHazardTable.columns;
            this.selectedRows =
              this.machinesCountAndInvestmentsByHazardTable.rows;
            break;
          case "Investimentos Realizados":
            await this.loadInvestedPercentileByHazard();
            this.selectedColumns = this.investmentsTable.columns;
            this.selectedRows = this.investmentsTable.rows;
            break;
        }
      },
      immediate: true,
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
